import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus } from "lucide-react";
import { mutate } from "swr";
import { SkillRow } from "~/components/assistant/SkillRow";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { Agent, AgentScope } from "~/services/agents";
import {
  PartialSkill,
  SKILL_LIST_LIMIT,
  SkillPage,
  agentSkillsListPath,
  detachAgentSkill,
} from "~/services/skills";
import { AgentAddSkillDialog } from "./AgentAddSkillDialog";
import { AgentSkillEditorDialog, AgentSkillEditorMode } from "./AgentSkillEditorDialog";

type DialogState =
  | { kind: "none" }
  | { kind: "picker" }
  | { kind: "editor"; mode: AgentSkillEditorMode };

interface AgentSkillsTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view (no edit affordances, no tab
  // title — the entity name is already shown in the sidebar header).
  sessionSidebarView?: boolean;
}

export const AgentSkillsTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentSkillsTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const canEdit = !sessionSidebarView && canEditGrant;
  const { showError, showSuccess } = useToast();

  const skillsKey = agentSkillsListPath(agent.id, { limit: SKILL_LIST_LIMIT });
  const { data, isLoading } = useFetcher<SkillPage>(skillsKey);
  const skills = data?.items ?? [];

  const [dialog, setDialog] = useState<DialogState>({ kind: "none" });
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const refreshList = () => mutate(skillsKey);

  const handleAdd = () => setDialog({ kind: "picker" });

  const handlePickFromLibrary = (skill: PartialSkill) => {
    setDialog({
      kind: "editor",
      mode: {
        kind: "duplicate",
        agentId: agent.id,
        agentDisplayName: agent.display_name,
        sourceId: skill.id,
      },
    });
  };

  const handleCreateNew = () =>
    setDialog({
      kind: "editor",
      mode: { kind: "create", agentId: agent.id, agentDisplayName: agent.display_name },
    });

  const handleEdit = (skill: PartialSkill) =>
    setDialog({ kind: "editor", mode: { kind: "edit", skillId: skill.id } });

  const handleEditorClose = () => setDialog({ kind: "none" });

  const handleSaved = async () => {
    setDialog({ kind: "none" });
    await refreshList();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeleting(true);
    try {
      await detachAgentSkill(agent.id, deleteTarget.id);
      showSuccess(`"${deleteTarget.name}" removed`);
      setDeleteTarget(null);
      await refreshList();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to remove skill";
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader
          title={sessionSidebarView ? undefined : "Skills"}
          showReadOnlyBadge={!sessionSidebarView && agent.scope === AgentScope.SYSTEM}
          context={
            isLoading
              ? undefined
              : `${skills.length} installed · Markdown instructions the agent reads on every turn`
          }
          description="Skills are domain-specific instructions and workflows the agent applies when relevant. Add ones from the library or write your own."
          action={
            canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAdd}
                className="text-primary hover:text-primary/80"
              >
                <Plus />
                Add skill
              </Button>
            )
          }
        />

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : skills.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No skills installed.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit={canEdit}
                onEdit={() => handleEdit(skill)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        )}
      </div>

      <AgentAddSkillDialog
        open={dialog.kind === "picker"}
        onOpenChange={(open) => !open && setDialog({ kind: "none" })}
        onPick={handlePickFromLibrary}
        onCreateNew={handleCreateNew}
      />

      <AgentSkillEditorDialog
        mode={dialog.kind === "editor" ? dialog.mode : null}
        onOpenChange={(open) => !open && handleEditorClose()}
        onSaved={handleSaved}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && !isDeleting && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this skill?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  &ldquo;{deleteTarget.name}&rdquo; will be removed from {agent.display_name}. This
                  does not affect the library source it was copied from.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
